import { useChartDataTable } from '../useChartDataTable';
import * as ChartDataTableUI from '../ChartDataTableUI';
import { isNil, isNumber } from 'lodash';
import {
  useTableColumnSortState,
  useTableColumnWidthState,
} from '../../../MultiGridTable';
import { GridCellProps } from 'react-virtualized';
import {
  calculateHeaderRowsOffset,
  calculateMeasureIndex,
  getColumnAggregatedValue,
  renderPercentageText,
} from '../utils';
import { formatNumberWithComma } from '../../../../utils/formatText';
import {
  defaultNumberFormatOptions,
  formatValueWithMeasureFormat,
  globalFormat,
} from '../../../../utils/formatting';
import { getTextWidth } from '../../utils';
import { IDisplayedMeasure, ITableSettings } from '../../interfaces';
import HeaderCellRenderer from './HeaderCellRenderer';
import SegmentHeaderRenderer from './SegmentHeaderRenderer';
import ComparisonHeaderRenderer from './ComparisonHeaderRenderer';
import BreakdownHeaderRenderer from './BreakdownHeaderRenderer';
import { colors } from '../../../../config/styles';

interface CellRendererProps extends GridCellProps {
  analysisId: string;
  dataTableData: ReturnType<typeof useChartDataTable>;
  columnWidthsData: ReturnType<typeof useTableColumnWidthState>;
  columnSortStateData: ReturnType<typeof useTableColumnSortState>;
  tableSettings: ITableSettings;
  filteredRows: any;
  displayedMeasures: IDisplayedMeasure[];
}

const FUNCTION_KEYS_WITH_NO_PERCENTAGE = [
  'MAX',
  'MIN',
  'MEDIAN',
  'AVG',
  'VALUE',
  'PERCENTILE',
];

const CellRenderer = ({
  analysisId,
  dataTableData,
  columnWidthsData,
  columnSortStateData,
  filteredRows,
  tableSettings,
  displayedMeasures,
  ...gridCellProps
}: CellRendererProps) => {
  const {
    rows,
    hasBreakdown,
    hasSegment,
    hasComparison,
    measures,
    segments,
    breakdowns,
  } = dataTableData;

  const { getColumnWidth } = columnWidthsData;
  const { columnIndex, rowIndex, style } = gridCellProps;

  if (rowIndex === 0) {
    return (
      <HeaderCellRenderer
        analysisId={analysisId}
        gridCellProps={gridCellProps}
        dataTableData={dataTableData}
        columnWidthsData={columnWidthsData}
        columnSortStateData={columnSortStateData}
        tableSettings={tableSettings}
      />
    );
  }

  let currentRowIndex = 1;

  if (hasSegment) {
    if (rowIndex === currentRowIndex) {
      return (
        <SegmentHeaderRenderer
          gridCellProps={gridCellProps}
          dataTableData={dataTableData}
          columnWidthsData={columnWidthsData}
          columnSortStateData={columnSortStateData}
          tableSettings={tableSettings}
        />
      );
    }
    currentRowIndex += 1;
  }

  if (hasComparison) {
    if (rowIndex === currentRowIndex) {
      return (
        <ComparisonHeaderRenderer
          gridCellProps={gridCellProps}
          dataTableData={dataTableData}
          columnWidthsData={columnWidthsData}
          columnSortStateData={columnSortStateData}
          tableSettings={tableSettings}
        />
      );
    }
    currentRowIndex += 1;
  }

  if (hasBreakdown) {
    if (rowIndex === currentRowIndex) {
      return (
        <BreakdownHeaderRenderer
          gridCellProps={gridCellProps}
          dataTableData={dataTableData}
          columnWidthsData={columnWidthsData}
          columnSortStateData={columnSortStateData}
          tableSettings={tableSettings}
        />
      );
    }
    currentRowIndex += 1;
  }

  // calculate the offset for the first row of rendering non-header data
  const rowIndexOffset = calculateHeaderRowsOffset(
    true,
    hasBreakdown,
    hasSegment,
    hasComparison,
  );
  const cellRowIndex = rowIndex - rowIndexOffset;
  const cellData = filteredRows[cellRowIndex][columnIndex];

  if (columnIndex < breakdowns.length) {
    return (
      <ChartDataTableUI.Cell
        key={`Data-Table-Cell-Row-${rowIndex}-Col-${columnIndex}`}
        style={style}
        $rowIndex={cellRowIndex}
        $columnIndex={columnIndex}
        // if kobol return null, show empty string.
        title={cellData?.toString() ?? ''}
      >
        {cellData}
      </ChartDataTableUI.Cell>
    );
  }

  const colIndex = columnIndex - breakdowns.length;
  const numComparisonColumns = tableSettings.showPercentDelta ? 3 : 2;
  const measureIndex = calculateMeasureIndex(
    colIndex,
    segments,
    hasComparison,
    numComparisonColumns,
  );
  const measure = measures[measureIndex];
  const displayedMeasure = displayedMeasures[measureIndex];

  let cellValue = null;
  if (!isNil(cellData)) {
    if (!isNil(displayedMeasure?.format) && isNumber(cellData)) {
      cellValue = formatValueWithMeasureFormat(
        cellData,
        displayedMeasure.format,
      );
    } else {
      cellValue = formatNumberWithComma(cellData).toString();
    }
  }

  // Check if it is the percent delta column
  if (
    hasComparison &&
    tableSettings.showPercentDelta &&
    colIndex % numComparisonColumns === 2
  ) {
    const percentageText = renderPercentageText(cellData);
    const color = cellData > 0 ? 'green' : cellData < 0 ? 'red' : 'inherit';
    const backgroundColor =
      cellData > 0
        ? colors.backgroundGreen // light green shade
        : cellData < 0
          ? colors.backgroundRed // light red shade
          : 'inherit'; // no shade for 0
    return (
      <ChartDataTableUI.Cell
        key={`Data-Table-Cell-Row-${rowIndex}-Col-${columnIndex}`}
        style={{ ...style, color, backgroundColor }}
        $rowIndex={cellRowIndex}
        $columnIndex={columnIndex}
        $isRightAligned={true}
        title={percentageText.toString()}
      >
        {percentageText}
      </ChartDataTableUI.Cell>
    );
  }

  // show horizontal bar if it is a simple measure
  if (
    measure?.__typename === 'SimpleMeasure' &&
    isNumber(cellData) &&
    breakdowns.length > 0
  ) {
    const max = getColumnAggregatedValue(
      rows,
      columnIndex,
      measure.function.key,
    );

    if (
      !isNil(max) &&
      !FUNCTION_KEYS_WITH_NO_PERCENTAGE.includes(measure.function.key)
    ) {
      const percentage = max !== 0 ? cellData / max : 0;
      const formattedPercentage = globalFormat.formatNumber(
        percentage,
        defaultNumberFormatOptions.percent,
      );

      const maxCssWidth = getColumnWidth({ index: columnIndex });
      const text = `${cellValue} (${formattedPercentage})`;
      const textWidth = getTextWidth(text, '12px') + 32; // padding
      const barWidthNumber = 1 + (cellData / max) * (maxCssWidth - textWidth);

      return (
        <ChartDataTableUI.Cell
          key={`Data-Table-Cell-Row-${rowIndex}-Col-${columnIndex}`}
          style={style}
          $rowIndex={cellRowIndex}
          $columnIndex={columnIndex}
          $isRightAligned={true}
          $withBar={true}
          title={text}
        >
          <ChartDataTableUI.Bar style={{ width: barWidthNumber }} />
          {text}
        </ChartDataTableUI.Cell>
      );
    }
  }

  if (!isNil(cellValue)) {
    return (
      <ChartDataTableUI.Cell
        key={`Data-Table-Cell-Row-${rowIndex}-Col-${columnIndex}`}
        style={style}
        $rowIndex={cellRowIndex}
        $columnIndex={columnIndex}
        $isRightAligned={true}
        title={cellValue}
      >
        {cellValue}
      </ChartDataTableUI.Cell>
    );
  }

  // return nil case
  return (
    <ChartDataTableUI.Cell
      key={`Data-Table-Cell-Row-${rowIndex}-Col-${columnIndex}`}
      style={style}
      $rowIndex={cellRowIndex}
      $columnIndex={columnIndex}
      $isRightAligned={true}
    >
      -
    </ChartDataTableUI.Cell>
  );
};

export default CellRenderer;
